import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";

const PROFILE_IMAGE = "https://static.readdy.ai/image/98c8f721efe8e85064ec351cb642a7fc/da29beaa06c6fb8302966195ed940516.png";

const OCCA_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "OCCA",
  jobTitle: "DJ",
  description:
    "北海道/札幌のPrecious HallでARCHIVをホームとする日本テクノシーンの特異点的存在。独自のサイケデリクスを探求し、ダンスフロアに多次元構造を生み出す。Spekki Webu、System Revival、Axces Recordingsと連携。",
  homeLocation: {
    "@type": "Place",
    name: "Hokkaido / Sapporo, Japan",
    geo: { "@type": "GeoCoordinates", latitude: 43.0618, longitude: 141.3545 },
  },
  url: `${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/artist/occa`,
  memberOf: [
    { "@type": "MusicGroup", name: "ARCHIV" },
    { "@type": "Organization", name: "Spekki Webu" },
  ],
};

const affiliations = [
  { label: "Home", value: "ARCHIV @ Precious Hall, Sapporo" },
  { label: "Organize", value: "Telepath / Womb  ·  Sect / Mitsuki" },
  { label: "Network", value: "Spekki Webu  ·  System Revival  ·  Axces Recordings" },
  { label: "Base", value: "Hokkaido / Sapporo, Japan" },
];

export default function OccaPage() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  /* ── Psychedelic Fractal Canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      /* Grid */
      const COLS = 16;
      const ROWS = 20;
      for (let c = 0; c <= COLS; c++) {
        const x = (c / COLS) * W;
        const pulse = Math.sin(t * 0.01 + c * 0.35) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.strokeStyle = `rgba(255,220,140,${0.012 + pulse * 0.015})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      for (let r = 0; r <= ROWS; r++) {
        const y = (r / ROWS) * H;
        const pulse = Math.sin(t * 0.008 + r * 0.28) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.strokeStyle = `rgba(255,220,140,${0.008 + pulse * 0.012})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* Psychedelic concentric rings — left side */
      const cx = W * 0.1;
      const cy = H * 0.5;
      for (let r = 0; r < 8; r++) {
        const radius = 20 + r * 30 + Math.sin(t * 0.01 + r * 0.7) * 8;
        const alpha = (0.07 - r * 0.007) * (Math.sin(t * 0.013 + r * 0.9) * 0.5 + 0.5);
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* Spiral arms — right side */
      const sx = W * 0.9;
      const sy = H * 0.3;
      for (let arm = 0; arm < 3; arm++) {
        ctx.beginPath();
        for (let i = 0; i < 120; i++) {
          const angle = (i / 20) * Math.PI * 2 + (arm * Math.PI * 2) / 3 + t * 0.008;
          const radius2 = i * 1.2;
          const x = sx + Math.cos(angle) * radius2;
          const y = sy + Math.sin(angle) * radius2;
          const alpha = (1 - i / 120) * 0.06;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
          ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        }
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      /* DNA helix — right edge */
      const helixX = W * 0.96;
      const helixAmplitude = 18;
      const helixStep = 16;
      const helixCount = Math.ceil(H / helixStep) + 2;
      for (let i = 0; i < helixCount; i++) {
        const y = i * helixStep - (t * 0.35 % helixStep);
        const phase = (i / helixCount) * Math.PI * 8 + t * 0.016;
        const x1 = helixX + Math.sin(phase) * helixAmplitude;
        const x2 = helixX + Math.sin(phase + Math.PI) * helixAmplitude;
        const alpha = Math.abs(Math.sin(phase)) * 0.3 + 0.04;
        if (i > 0) {
          const prevY = (i - 1) * helixStep - (t * 0.35 % helixStep);
          const prevPhase = ((i - 1) / helixCount) * Math.PI * 8 + t * 0.016;
          const prevX1 = helixX + Math.sin(prevPhase) * helixAmplitude;
          ctx.beginPath();
          ctx.moveTo(prevX1, prevY);
          ctx.lineTo(x1, y);
          ctx.strokeStyle = `rgba(255,220,140,${alpha * 0.6})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
          const prevX2 = helixX + Math.sin(prevPhase + Math.PI) * helixAmplitude;
          ctx.beginPath();
          ctx.moveTo(prevX2, prevY);
          ctx.lineTo(x2, y);
          ctx.strokeStyle = `rgba(255,200,100,${alpha * 0.4})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
        if (i % 3 === 0) {
          ctx.beginPath();
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
          ctx.strokeStyle = `rgba(255,220,140,${alpha * 0.2})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }

      /* Scan line */
      const scanY = ((t * 0.45) % H);
      const scanGrad = ctx.createLinearGradient(0, scanY - 60, 0, scanY + 60);
      scanGrad.addColorStop(0, "rgba(255,220,140,0)");
      scanGrad.addColorStop(0.5, "rgba(255,220,140,0.02)");
      scanGrad.addColorStop(1, "rgba(255,220,140,0)");
      ctx.fillStyle = scanGrad;
      ctx.fillRect(0, scanY - 60, W, 120);

      t++;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".reveal");
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = "1";
                (item as HTMLElement).style.transform = "translateY(0)";
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="relative min-h-screen w-full overflow-x-hidden"
      style={{ background: "#060606" }}
    >
      <SeoHead
        title="OCCA | Artist Profile | MAVERICK vol.5"
        description="OCCAのアーティストプロフィール。北海道/札幌拠点のテクノDJ。Precious HallのARCHIVをホームに、独自のサイケデリクスでダンスフロアに多次元構造を生み出す日本テクノシーンの特異点的存在。"
        keywords="OCCA, Sapporo techno DJ, ARCHIV, Precious Hall, psychedelic techno Japan, Spekki Webu"
        canonical={`${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/artist/occa`}
        ogImage="https://static.readdy.ai/image/98c8f721efe8e85064ec351cb642a7fc/da29beaa06c6fb8302966195ed940516.png"
        jsonLd={OCCA_JSONLD}
      />
      <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none z-0" />

      {/* Back button */}
      <button
        onClick={() => navigate("/")}
        className="fixed top-7 left-7 md:left-12 z-50 flex items-center gap-3 group cursor-pointer whitespace-nowrap"
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        <div
          className="w-8 h-8 flex items-center justify-center border rounded-full"
          style={{ borderColor: "rgba(255,220,140,0.2)" }}
        >
          <i className="ri-arrow-left-line" style={{ color: "rgba(255,220,140,0.5)", fontSize: "14px" }} />
        </div>
        <span className="text-[10px] tracking-[0.35em] uppercase hidden md:block" style={{ color: "rgba(255,220,140,0.3)" }}>
          Back
        </span>
      </button>

      <div ref={sectionRef} className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-20 pt-24 md:pt-32 pb-32">

        {/* ── HERO BLOCK ── */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start mb-24 md:mb-36">

          {/* Profile image */}
          <div
            className="reveal flex-shrink-0 relative w-full lg:w-auto"
            style={{ opacity: 0, transform: "translateY(24px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <div className="absolute -inset-3 pointer-events-none" style={{ border: "1px solid rgba(255,220,140,0.08)", borderRadius: "2px" }} />
            <div className="absolute -top-1 -left-1 w-6 h-6 pointer-events-none" style={{ borderTop: "1px solid rgba(255,220,140,0.35)", borderLeft: "1px solid rgba(255,220,140,0.35)" }} />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 pointer-events-none" style={{ borderBottom: "1px solid rgba(255,220,140,0.35)", borderRight: "1px solid rgba(255,220,140,0.35)" }} />
            <div className="overflow-hidden w-full lg:w-auto" style={{ height: "clamp(260px, 55vw, 480px)", maxWidth: "360px" }}>
              <img
                src={PROFILE_IMAGE}
                alt="OCCA"
                className="w-full h-full object-cover object-top"
                style={{ filter: "grayscale(15%) contrast(1.05)" }}
              />
            </div>
            <div className="flex items-center gap-2 mt-4" style={{ fontFamily: "'Courier New', monospace" }}>
              <div style={{ width: "12px", height: "1px", background: "rgba(255,220,140,0.25)" }} />
              <span className="text-[9px] tracking-[0.3em] uppercase" style={{ color: "rgba(255,220,140,0.2)" }}>
                JP — 43.0618° N
              </span>
            </div>
          </div>

          {/* Text block */}
          <div className="flex flex-col gap-8 flex-1 pt-2 lg:pt-6">
            <div
              className="reveal flex items-center gap-4"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              <div style={{ width: "20px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
              <span className="text-[10px] tracking-[0.5em] uppercase" style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}>
                Artist Profile
              </span>
            </div>

            <div
              className="reveal"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              <h1
                className="font-light leading-none tracking-wider"
                style={{
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontSize: "clamp(36px, 6vw, 80px)",
                  fontWeight: 200,
                  color: "rgba(255,255,255,0.92)",
                  letterSpacing: "0.06em",
                  textShadow: "0 0 60px rgba(255,220,140,0.1)",
                }}
              >
                OCCA
              </h1>
              <div className="flex items-center gap-3 mt-3">
                <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "rgba(255,220,140,0.6)", boxShadow: "0 0 10px 2px rgba(255,200,100,0.25)" }} />
                <span className="text-xs tracking-[0.3em] uppercase" style={{ color: "rgba(255,220,140,0.35)", fontFamily: "'Courier New', monospace" }}>
                  Hokkaido / Sapporo, Japan
                </span>
              </div>
            </div>

            <div
              className="reveal flex flex-wrap gap-2"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              {["Techno", "Psychedelics", "ARCHIV", "Precious Hall", "Spekki Webu"].map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-[10px] tracking-[0.25em] uppercase"
                  style={{ border: "1px solid rgba(255,220,140,0.12)", color: "rgba(255,220,140,0.35)", fontFamily: "'Courier New', monospace", borderRadius: "1px" }}
                >
                  {tag}
                </span>
              ))}
            </div>

            <div
              className="reveal"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              <p
                className="leading-relaxed"
                style={{
                  fontSize: "clamp(15px, 1.1vw, 17px)",
                  color: "rgba(255,255,255,0.5)",
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontWeight: 300,
                  letterSpacing: "0.02em",
                  lineHeight: "2.0",
                }}
              >
                日本の北海道/札幌に在り聖域と称されるPrecious Hallでの「ARCHIV」をホームとしながら、Telepath/Womb、Sect/Mitsukiなどのオーガナイズに加え、未知なる表現を探求し続ける日本テクノシーンにおける特異点的存在。
              </p>
              <p
                className="leading-relaxed mt-5"
                style={{
                  fontSize: "clamp(15px, 1.1vw, 17px)",
                  color: "rgba(255,255,255,0.38)",
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontWeight: 300,
                  letterSpacing: "0.02em",
                  lineHeight: "2.0",
                }}
              >
                独自のサイケデリクスを探求する中で吸収してきた多様な音楽観と精神性を、能動的なミキシングと緻密な構築力で形象化し、ダンスフロアに多次元構造を生み出す。空間や時間感覚さえも分解し、体験者の知覚情報処理能力を拡張する事を目的としている。
              </p>
              <p
                className="leading-relaxed mt-5"
                style={{
                  fontSize: "clamp(15px, 1.1vw, 17px)",
                  color: "rgba(255,255,255,0.28)",
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontWeight: 300,
                  letterSpacing: "0.02em",
                  lineHeight: "2.0",
                }}
              >
                その類稀な存在を見出され、アジア・ヨーロッパ諸国での定期的なブッキングや、国内外の主要クラブ/フェスティバルでのプレイを重ねてきた。近年は Spekki Webu、System Revival、Axces Recordings と深く共鳴し、彼のサイケデリックネットワークは新たなフェイズへと突入している。
              </p>
            </div>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div
          className="reveal w-full mb-24 md:mb-32"
          style={{
            opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease",
            height: "1px", background: "linear-gradient(to right, transparent, rgba(255,220,140,0.12), transparent)",
          }}
        />

        {/* ── AFFILIATIONS ── */}
        <div className="mb-24 md:mb-32">
          <div
            className="reveal flex items-center gap-4 mb-12"
            style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <div style={{ width: "20px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
            <span className="text-[10px] tracking-[0.5em] uppercase" style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}>
              Affiliations
            </span>
          </div>

          <div className="flex flex-col">
            {affiliations.map((item, i) => (
              <div
                key={i}
                className="reveal group relative py-5 border-b overflow-hidden"
                style={{ borderColor: "rgba(255,220,140,0.06)", opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
              >
                <div className="absolute left-0 top-0 bottom-0 w-0 group-hover:w-full transition-all duration-700 pointer-events-none" style={{ background: "linear-gradient(to right, rgba(255,220,140,0.03), transparent)" }} />
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-8 sm:items-baseline">
                  <span className="text-white/20 text-[10px] tracking-[0.3em] uppercase flex-shrink-0 sm:w-28" style={{ fontFamily: "'Courier New', monospace" }}>
                    {item.label}
                  </span>
                  <span className="font-light text-sm md:text-base" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 300, color: "rgba(255,255,255,0.5)", letterSpacing: "0.04em" }}>
                    {item.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── MIX PLAYER ── */}
        <div className="mb-24 md:mb-32">
          <div
            className="reveal flex items-center gap-4 mb-10"
            style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <div style={{ width: "20px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
            <span
              className="text-[10px] tracking-[0.5em] uppercase"
              style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}
            >
              Listen
            </span>
          </div>

          <div
            className="reveal relative"
            style={{
              opacity: 0,
              transform: "translateY(20px)",
              transition: "opacity 1.2s ease, transform 1.2s ease",
              border: "1px solid rgba(255,220,140,0.08)",
            }}
          >
            <div className="absolute top-0 left-0 w-4 h-4 pointer-events-none" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
            <div className="absolute top-0 right-0 w-4 h-4 pointer-events-none" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />
            <div className="absolute bottom-0 left-0 w-4 h-4 pointer-events-none" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
            <div className="absolute bottom-0 right-0 w-4 h-4 pointer-events-none" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />

            {/* SoundCloud embed */}
            <iframe
              title="OCCA on SoundCloud"
              width="100%"
              height="450"
              allow="autoplay"
              src="https://w.soundcloud.com/player/?url=https%3A//on.soundcloud.com/nKjGhZ3bn7FDE38crQ&color=%23ffdc8c&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=true&visual=true"
              style={{ border: "none", display: "block", background: "#0a0a0a" }}
            />
          </div>

          <div
            className="reveal flex justify-end mt-4"
            style={{ opacity: 0, transform: "translateY(12px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <a
              href="https://on.soundcloud.com/nKjGhZ3bn7FDE38crQ"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-2 cursor-pointer whitespace-nowrap"
              style={{ textDecoration: "none" }}
            >
              <i className="ri-soundcloud-line" style={{ color: "rgba(255,220,140,0.3)", fontSize: "13px" }} />
              <span
                className="text-[9px] tracking-[0.35em] uppercase"
                style={{ color: "rgba(255,220,140,0.25)", fontFamily: "'Courier New', monospace" }}
              >
                View on SoundCloud
              </span>
              <i className="ri-external-link-line" style={{ color: "rgba(255,220,140,0.2)", fontSize: "10px" }} />
            </a>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div
          className="reveal w-full mb-24 md:mb-32"
          style={{
            opacity: 0,
            transform: "translateY(16px)",
            transition: "opacity 1.2s ease, transform 1.2s ease",
            height: "1px",
            background: "linear-gradient(to right, transparent, rgba(255,220,140,0.12), transparent)",
          }}
        />

        {/* ── QUOTE ── */}
        <div
          className="reveal relative mb-24 md:mb-32 px-5 md:px-16 py-10 md:py-16"
          style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease", border: "1px solid rgba(255,220,140,0.07)" }}
        >
          <div className="absolute top-0 left-0 w-5 h-5" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
          <div className="absolute top-0 right-0 w-5 h-5" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />
          <div className="absolute bottom-0 left-0 w-5 h-5" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
          <div className="absolute bottom-0 right-0 w-5 h-5" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />
          <p
            className="text-center font-light leading-relaxed mb-6"
            style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: "clamp(14px, 1.8vw, 22px)", fontWeight: 200, color: "rgba(255,255,255,0.55)", letterSpacing: "0.04em", lineHeight: "1.8", fontStyle: "italic" }}
          >
            &ldquo;空間や時間感覚さえも分解し、体験者の知覚情報処理能力を拡張する&rdquo;
          </p>
          <p className="text-center text-[10px] tracking-[0.4em] uppercase" style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}>
            — OCCA
          </p>
        </div>

        {/* ── BACK ── */}
        <div className="reveal flex justify-center" style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-4 px-8 py-4 cursor-pointer whitespace-nowrap"
            style={{ border: "1px solid rgba(255,220,140,0.18)", fontFamily: "'Courier New', monospace", color: "rgba(255,220,140,0.4)", fontSize: "11px", letterSpacing: "0.35em", textTransform: "uppercase", background: "transparent" }}
          >
            <i className="ri-arrow-left-line" style={{ fontSize: "12px" }} />
            Back to Lineup
          </button>
        </div>
      </div>
    </div>
  );
}
